import { useEffect, useRef } from 'react';
import cytoscape from 'cytoscape';
import coseBilkent from 'cytoscape-cose-bilkent';

cytoscape.use(coseBilkent);

interface GraphCanvasProps {
  elements: cytoscape.ElementDefinition[];
  openItem: (itemId: string) => void;
  layoutSettings: {
    nodeRepulsion: number;
    idealEdgeLength: number;
    edgeElasticity: number;
    initialEnergyOnIncremental: number;
  };
}

function GraphCanvas({ elements, openItem, layoutSettings }: GraphCanvasProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cyRef = useRef<cytoscape.Core | null>(null);
  const openingItemIdRef = useRef<string|null>(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const cy = cytoscape({
      container: containerRef.current,
      elements: [],
      style: [
        {
          selector: 'node',
          style: {
            'shape': 'round-rectangle',
            'background-color': 'data(color)',
            'label': 'data(label)',
            'color': '#101010',
            'font-size': 15,
            'text-wrap': 'wrap',
            'text-valign': 'center',
            'text-halign': 'center',
            'padding': '5px',
            'width': 'label',
            'height': 'label',
            'min-zoomed-font-size': 8,
            'border-color': '#101010',
            'border-width': 1,
            'border-opacity': 0.5,
          }
        },
        {
          selector: 'node[?archived]',
          style: {
            'background-color': '#101010',
            'color': 'data(color)',
            'border-color': 'data(color)',
            'opacity': 0.75,
          }
        },
        {
          selector: 'node[?neighbour]',
          style: {
            'font-size': 10,
            'border-style': 'dotted',
            'border-width': 2,
            'border-opacity': 0.9,
            //'opacity': 0.5,
            'background-blacken': 0.5,
            'border-color': '#888',
          }
        },
        {
          selector: 'node[?neighbour][?archived]',
          style: {
            'opacity': 0.5,
            'border-color': '#ccc'
          }
        },
        {
          selector: 'edge',
          style: {
            'width': 2,
            'line-color': 'data(color)',
            'line-opacity': 0.7,
            'target-arrow-color': 'data(color)',
            'target-arrow-shape': 'triangle',
            'curve-style': 'straight',
          }
        },
        {
          selector: 'edge.bidirectional',
          style: {
            'width': 2,
            'line-color': 'data(color)',
            'line-opacity': 0.7,
            'target-arrow-color': 'data(color)',
            'target-arrow-shape': 'triangle',
            'source-arrow-color': 'data(color)',
            'source-arrow-shape': 'triangle',
            'curve-style': 'straight'
          }
        },
        {
          selector: 'edge.implicit',
          style: {
            'line-style': 'dashed',
            'line-dash-pattern': [6, 4],
          }
        },
        {
          selector: 'edge.neighbour',
          style: {
            'line-opacity': 0.30,
            'line-style': 'dashed',
            'line-dash-pattern': [2, 4],
          }
        },
        {
          selector: 'edge.highlighted',
          style: {
            'width': 3,
            'line-opacity': 1.0,
            'line-color': '#aa6',
            'target-arrow-color': '#aa6',
            'source-arrow-color': '#aa6',
            'z-index': 9999
          }
        }
      ],
      wheelSensitivity: 0.2,
      minZoom: 0.4,
      maxZoom: 2
    });

    cy.on('tap', 'node', (event: any) => {
      const itemId = event.target.id();
      openingItemIdRef.current = itemId;
      openItem(itemId);
    });

    cy.on('mouseover', 'node', (event: any) => {
      const node = event.target;
      const connectedEdges = node.connectedEdges();
      connectedEdges.addClass('highlighted');
      const container = cy.container();
      if (container) {
        container.style.cursor = 'pointer';
      }
    });

    cy.on('mouseout', 'node', (event: any) => {
      const container = cy.container();
      if (container) {
        container.style.cursor = 'default';
      }
    });

    const removeHighlight = () => {
      cy.edges().removeClass('highlighted');
    };

    cy.on('mouseout', removeHighlight);

    cyRef.current = cy;

    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, [openItem]);

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) {
      return;
    }

    if (openingItemIdRef.current) {
      // Retain position of nodes that are already present in the graph
      for (const element of elements) {
        const existingElement = cy.getElementById(element.data.id ?? "");
        if (!existingElement.empty()) {
          element.position = existingElement.position();
        }
      }
    }
    cy.elements().remove();
    cy.add(elements);

    const nodesWithDegreeGreaterThanTwo = cy.nodes().filter((node) => node.degree() > 2);
    const nodeCount = Math.max(nodesWithDegreeGreaterThanTwo.length, 1);
    const edgeCount = nodesWithDegreeGreaterThanTwo.connectedEdges().filter((edge) => {
      return edge.source().degree() > 2 && edge.target().degree() > 2;
    }).length;
    const averageDegreeFactor = Math.min(8, Math.max(1, edgeCount / nodeCount));
    const actualIdealEdgeLength = layoutSettings.idealEdgeLength * averageDegreeFactor;

    const layout = cy.layout({
      name: 'cose-bilkent',
      quality: 'proof',
      animate: !!openingItemIdRef.current,
      fit: true,
      padding: 0,
      nodeDimensionsIncludeLabels: true,
      randomize: false,
      nodeRepulsion: layoutSettings.nodeRepulsion,
      idealEdgeLength: actualIdealEdgeLength,
      edgeElasticity: layoutSettings.edgeElasticity,
      //gravity: 1.0, // Doesn't seem to matter, default: 0.25
      //gravityRange: 3.8, // Doesn't seem to matter, default: 3.8
      initialEnergyOnIncremental: layoutSettings.initialEnergyOnIncremental,
      numIter: 10000,
    } as any);

    layout.run();
    openingItemIdRef.current = null;
  }, [elements, layoutSettings.nodeRepulsion, layoutSettings.idealEdgeLength, layoutSettings.edgeElasticity, layoutSettings.initialEnergyOnIncremental]);

  return <div className="graphviewcanvas" ref={containerRef} />;
}

export default GraphCanvas;
